import { createRef, Fragment, PureComponent } from 'react';
import autoBind from 'react-autobind';
import moment from 'moment';
import { connect } from 'react-redux';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp } from '@fortawesome/pro-light-svg-icons/faChevronUp';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import CustomTimePeriod from '../../StoryFilters/TimePeriodFilter/CustomTimeFilter/CustomTimePeriod';
import RoundRadioButton from '../../GeneralComponents/RoundRadioButton';
import Tooltip from '../../Tooltip';

import withComponentName from '../../../decorators/withComponentName';

import Styles from './styles.module.scss';

import 'react-datepicker/dist/react-datepicker.css';

const cx = classNames.bind(Styles);

const tooltipContentStyle = {
  transform: 'translate(calc(-50% + 8px), calc(-100% - 10px))',
  fontWeight: 'normal',
  textTransform: 'none',
  zIndex: 1051,
};

export class TimePeriodFilter extends PureComponent {
  constructor(props) {
    super(props);
    autoBind(this);
    this.state = {
      isDatePickerOpen: false,
      startDate: '',
      endDate: '',
      displayedName: '',
      buttonErr: false,
      earliestSearchDate: moment([1, 0, 1]).toDate(),
      minDate: moment([1864, 0, 1]).toDate(),
      dateFilterType: 'lifetime',
    };
    this.datePicker = createRef();
    this.datepickerBoxRef = createRef();
  }

  componentDidMount() {
    document.addEventListener('click', this.handleClickOutside);
    this.setStateData();
  }

  componentDidUpdate(prevProps) {
    const {
      startDate: propsStartDate,
      endDate: propsEndDate,
      dateFilterType: propsDateFilterType,
    } = this.props;

    if (
      propsStartDate !== prevProps.startDate
      || propsEndDate !== prevProps.endDate
      || propsDateFilterType !== prevProps.dateFilterType
    ) {
      this.setStateData();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  handleClickOutside(e) {
    const { isDatePickerOpen } = this.state;

    if (
      e.target
      && isDatePickerOpen
      && (
        (this.datePicker.current && !this.datePicker.current.contains(e.target))
        && (
          this.datepickerBoxRef.current
          && !this.datepickerBoxRef.current.contains(e.target)
        )
      )
    ) {
      this.setState({
        isDatePickerOpen: false,
      });
    }
  }

  handleDatePickerOpenClick() {
    const { isDatePickerOpen } = this.state;

    this.setState({
      isDatePickerOpen: !isDatePickerOpen,
    });
  }

  setStateData() {
    let {
      startDate,
      endDate,
    } = this.props;
    const { dateFilterType } = this.props;

    const defaultStartDate = moment().add(-1, 'y').toDate();
    const defaultEndDate = moment().toDate();
    startDate = startDate ? moment(startDate).toDate() : defaultStartDate;
    endDate = endDate ? moment(endDate).toDate() : defaultEndDate;

    this.setState(({ dateFilterType: currentDateFilterType }) => ({
      startDate,
      endDate,
      dateFilterType: dateFilterType || currentDateFilterType,
      displayedName: `${moment(startDate).format('D MMM YYYY')} - ${moment(endDate).format('D MMM YYYY')}`,
    }));
  }

  setDateFilter({ start_datetime: startDate, end_datetime: endDate }) {
    const { onDateSelect } = this.props;
    const { dateFilterType } = this.state;

    const displayedName = `${moment(startDate).format('D MMM YYYY')} - ${moment(endDate).format('D MMM YYYY')}`;

    if (moment(startDate).format('DD-MM-YYYY') === moment(endDate).format('DD-MM-YYYY')) {
      this.setState({
        startDate: moment(startDate).startOf('day').toISOString(),
        endDate: moment(endDate).endOf('day').toISOString(),
        displayedName,
      });
    } else {
      this.setState({
        startDate,
        endDate,
        displayedName,
      });
    }

    onDateSelect({
      startDate,
      endDate,
      dateFilterType,
    });
  }

  setViewDateFilter({ type }) {
    const { onDateSelect } = this.props;
    const { startDate, endDate } = this.state;

    this.setState({
      dateFilterType: type,
    });

    onDateSelect({
      startDate,
      endDate,
      dateFilterType: type,
    });
  }

  showButtonError(buttonErr) {
    this.setState({ buttonErr });
  }

  render() {
    const {
      isDatePickerOpen, displayedName, startDate, endDate,
      buttonErr, earliestSearchDate, minDate, dateFilterType,
    } = this.state;
    const { className, width, tooltip } = this.props;

    const datepickerBoxClass = cx(Styles.datepicker_box, {
      show: isDatePickerOpen,
    });

    const startDatePass = startDate ? moment(startDate).toDate() : '';
    const endDatePass = endDate ? moment(endDate).toDate() : '';

    const PeriodWrapper = width <= 1024 ? Modal : Fragment;
    const periodWrapperProps = width <= 1024 ? {
      show: isDatePickerOpen,
      centered: true,
      onHide() {},
    } : {};

    return (
      <div
        className={cx('filings-filter-btn', 'time-period-wrapper', className)}
        ref={this.datePicker}
      >
        <button type="button" className={Styles.datepicker_header} onClick={this.handleDatePickerOpenClick}>
          <div className={Styles.header_holder}>
            <div className={Styles.header_value}>
              {dateFilterType === 'lifetime' ? 'Full Archive' : displayedName}
            </div>
            <div className={Styles.header_label}>Time Period</div>
          </div>
          <div className={Styles.header_icon}>
            <FontAwesomeIcon icon={isDatePickerOpen ? faChevronUp : faChevronDown} />
          </div>
        </button>
        <PeriodWrapper {...periodWrapperProps}>
          <div className={datepickerBoxClass} ref={this.datepickerBoxRef}>
            <div className={Styles.box_close}>
              <FontAwesomeIcon icon={faXmark} onClick={this.handleDatePickerOpenClick} />
            </div>
            <div className={Styles.box_period}>
              Period:
              {tooltip && (
                <Tooltip
                  className={Styles.tooltip}
                  tooltipClassName={Styles['tooltip-content']}
                  renderOutside
                  hideWithOverflow
                  contentStyle={tooltipContentStyle}
                  dynamicPosition
                >
                  {tooltip}
                </Tooltip>
              )}
            </div>
            <div className={Styles.box_lifetime_choice}>
              <RoundRadioButton
                name="Full Archive"
                value="lifetime"
                selectedFilterValue={dateFilterType}
                setValue={this.setViewDateFilter}
                filterItemInfo={{
                  type: 'lifetime',
                }}
              />
            </div>
            <div className={Styles.box_custom_choice}>
              <RoundRadioButton
                name="Date Range"
                value="custom"
                selectedFilterValue={dateFilterType}
                setValue={this.setViewDateFilter}
                filterItemInfo={{
                  type: 'custom',
                }}
              />
            </div>
            {isDatePickerOpen && (
              <CustomTimePeriod
                hideRoundButton
                isFilingsPage
                activeTimeFilter={dateFilterType}
                selectedFilterValue={{
                  start_datetime: startDatePass,
                  end_datetime: endDatePass,
                }}
                width={width}
                timeInputWrapperClass={cx('time-input-wrapper')}
                setTimeFilter={this.setDateFilter}
                showButtonError={this.showButtonError}
                buttonErr={buttonErr}
                earliestSearchDate={earliestSearchDate}
                errorsBlockClass={Styles.custom_errors_blocks}
                minDate={minDate}
              />
            )}
          </div>
        </PeriodWrapper>
      </div>
    );
  }
}

TimePeriodFilter.propTypes = {
  startDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  endDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  tooltip: PropTypes.node,
  onDateSelect: PropTypes.func,
  className: PropTypes.string,
};

TimePeriodFilter.defaultProps = {
  startDate: null,
  endDate: null,
  tooltip: null,
  onDateSelect() {},
  className: '',
};

const mapStateToProps = ({ common }) => ({
  width: common.width,
});

export default withComponentName(
  connect(mapStateToProps, null)(TimePeriodFilter),
);
